// Карта плотностей — zidurile din stakan pe un cerc, în jurul prețului.
//
// Centrul e prețul fiecărei monede; distanța de la centru — cât % are prețul de mers până
// la zid. **Sus sunt vânzările** (prețul trebuie să urce până la ele), **jos cumpărările** —
// dintr-o privire vezi de ce parte e sprijinul. Fiecare monedă își păstrează locul pe cerc
// (unghiul vine din nume), ca bulele să nu sară de la o citire la alta.
//
// Bula: mărimea — forța zidului (minute de oborot sau $), conturul — cât trăiește, pulsul —
// prețul se apropie. Zidurile terminate în ultimele două minute rămân ca fantome: „снята"
// (a dispărut înainte de preț — momeală) sau „съедена".
//
// Datele vin din `/api/walls` (stakan complet pe ~100 de monede volatile, Binance + Bybit),
// iar sub hartă stă jurnalul: ce au făcut de fapt zidurile când prețul a ajuns la ele.

import { el, clear, fmtVolume, fmtAge, fmtPrice } from '../core/util.js';
import { settings } from '../core/state.js';
import { iconMoneda, iconBursa } from './coin-icon.js';
import { icon } from './icons.js';
import { baseTicker, dropdown } from './components.js';

const KEY = 'sx_wallmap';
const REFRESH_MS = 2500;

/** Presetările: praguri alese pentru ce caută omul. */
const PRESETS = [
  { id: 'bounce', label: 'Отскок 1%', icon: 'fa-arrow-turn-up',
    title: 'Монеты с ходом ≥0.8% за свечу, плотность живёт ≥5 мин и стоит ≥2 минут оборота, до 1.5% от цены',
    f: { maxDistance: 1.5, minMinutes: 2, minAge: 300, minUsd: 0, minNatr: 0.8, minVol: 0, side: 'all' } },
  { id: 'strong', label: 'Сильные', icon: 'fa-dumbbell',
    title: 'Плотности на 5+ минут оборота монеты, живут ≥1 мин, до 2%',
    f: { maxDistance: 2, minMinutes: 5, minAge: 60, minUsd: 0, minNatr: 0, minVol: 0, side: 'all' } },
  { id: 'old', label: 'Долгожители', icon: 'fa-hourglass-end',
    title: 'Стоят 15 минут и дольше: такие реже снимают перед подходом',
    f: { maxDistance: 2, minMinutes: 1, minAge: 900, minUsd: 0, minNatr: 0, minVol: 0, side: 'all' } },
  { id: 'big', label: 'Крупные монеты', icon: 'coins',
    title: 'Оборот ≥ $100M и плотность от $100K',
    f: { maxDistance: 2, minMinutes: 1, minAge: 60, minUsd: 100e3, minNatr: 0, minVol: 100e6, side: 'all' } },
  { id: 'all', label: 'Все', icon: 'fa-border-all',
    title: 'Без фильтров: 90 самых сильных плотностей до 2%',
    f: { maxDistance: 2, minMinutes: 0, minAge: 0, minUsd: 0, minNatr: 0, minVol: 0, side: 'all' } },
];

const FIELDS = [
  { key: 'maxDistance', prefix: 'До цены', lead: 'fa-bullseye', title: 'Как далеко плотность от цены',
    opts: [[0.5, '0.5%'], [1, '1%'], [1.5, '1.5%'], [2, '2%'], [3, '3%']] },
  { key: 'minMinutes', prefix: 'Сила', lead: 'fa-dumbbell',
    title: 'Сколько минут среднего оборота монеты нужно, чтобы съесть плотность. Одинаково для BTC и мелкого альта: '
      + 'медиана — 1 мин, сильнее 90% плотностей — от 3 мин',
    opts: [[0, 'любая'], [1, '≥1 мин оборота'], [2, '≥2 мин'], [5, '≥5 мин'], [10, '≥10 мин']] },
  { key: 'minAge', prefix: 'Живёт', lead: 'fa-hourglass-half', title: 'Сколько плотность уже стоит',
    opts: [[0, 'любая'], [60, '≥1 мин'], [300, '≥5 мин'], [900, '≥15 мин'], [1800, '≥30 мин']] },
  { key: 'minUsd', prefix: 'Объём', lead: 'fa-sack-dollar', title: 'Размер плотности в долларах',
    opts: [[0, 'любой'], [20e3, '≥$20K'], [50e3, '≥$50K'], [100e3, '≥$100K'], [250e3, '≥$250K']] },
  { key: 'minNatr', prefix: 'Ход', lead: 'fa-wave-square',
    title: 'Средний ход монеты за 5-минутную свечу: от него зависит, дойдёт ли отскок до 1%',
    opts: [[0, 'любой'], [0.3, '≥0.3%'], [0.5, '≥0.5%'], [0.8, '≥0.8%'], [1.2, '≥1.2%']] },
  { key: 'minVol', prefix: 'Оборот', lead: 'coins', title: 'Оборот монеты за 24 часа',
    opts: [[0, 'любой'], [10e6, '≥$10M'], [50e6, '≥$50M'], [100e6, '≥$100M']] },
  { key: 'side', prefix: 'Сторона', lead: 'fa-arrows-up-down', title: 'Покупки — поддержка снизу, продажи — сопротивление сверху',
    opts: [['all', 'обе'], ['bid', 'покупки'], ['ask', 'продажи']] },
  { key: 'kind', prefix: 'Тип', lead: 'fa-user-shield',
    title: 'Реальные — стоят на месте ≥2 мин. Маркет-мейкер — переставляются вслед за ценой на том же расстоянии '
      + '(двигают график, отскока от них не будет). Молодые — меньше 2 мин, ещё непонятно',
    opts: [['real', 'реальные'], ['all', 'все'], ['mm', 'маркет-мейкер'], ['young', 'молодые']] },
  { key: 'exchange', prefix: 'Биржа', lead: 'fa-building-columns', title: 'Стакан какой биржи',
    opts: [['all', 'все'], ['binance', 'Binance'], ['bybit', 'Bybit'], ['okx', 'OKX']] },
  { key: 'size', prefix: 'Пузырь', lead: 'fa-circle', title: 'Что показывает размер пузыря',
    opts: [['minutes', 'по силе'], ['usd', 'по $']] },
];

const DEFAULTS = { ...PRESETS[0].f, exchange: 'all', size: 'minutes', preset: 'bounce', kind: 'real' };

/** Unghiul monedei pe semicerc, din nume: același nume — același loc. */
function hash01(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return ((h >>> 0) % 10007) / 10007;
}

const pct = (x) => `${x >= 0 ? '+' : '−'}${Math.abs(x).toFixed(2)}%`;
const min = (s) => (s >= 60 ? `${Math.floor(s / 60)}` : '<1');

export class DensityMap {
  constructor({ onSelect, onData } = {}) {
    this.onSelect = onSelect;
    this.onData = onData;
    this.f = { ...DEFAULTS, ...(settings.get(KEY, {}) || {}) };
    this.walls = [];
    this.ended = [];
    this.nodes = new Map();        // cheie -> { el, wall }
    this.selected = null;
  }

  mount(host) {
    this.root = el('div', { class: 'wm' });
    this.toolbar = el('div', { class: 'wm-tools' });
    this.stage = el('div', { class: 'wm-stage' });
    this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.svg.setAttribute('class', 'wm-rings');
    this.layer = el('div', { class: 'wm-layer' });
    this.tip = el('div', { class: 'wm-tip', hidden: true });
    this.emptyEl = el('div', { class: 'wm-empty', hidden: true });
    this.stage.append(this.svg, this.layer, this.tip, this.emptyEl);
    this.statsEl = el('div', { class: 'wm-stats' });
    this.root.append(this.toolbar, this.stage, this.#legend(), this.statsEl);
    host.append(this.root);
    this.#buildTools();

    this.ro = new ResizeObserver(() => this.#draw());
    this.ro.observe(this.stage);
    this.#fetch();
    this.#fetchStats();
    this.timer = setInterval(() => this.#fetch(), REFRESH_MS);
    this.statsTimer = setInterval(() => this.#fetchStats(), 60000);
    return this;
  }

  unmount() {
    clearInterval(this.timer);
    clearInterval(this.statsTimer);
    this.ro?.disconnect();
    this.root?.remove();
  }

  setSelected(key) {
    this.selected = key;
    for (const [k, n] of this.nodes) n.el.classList.toggle('sel', k === key);
  }

  // ----------------------------------------------------------------- bara

  #buildTools() {
    clear(this.toolbar);
    const items = (opts) => opts.map(([v, t]) => ({ value: String(v), label: t }));
    this.presetDD = dropdown({
      className: 'fx-dd', lead: 'sliders', prefix: 'Пресет',
      title: PRESETS.find((p) => p.id === this.f.preset)?.title || 'Свои настройки',
      items: [...PRESETS.map((p) => ({ value: p.id, label: p.label, icon: p.icon })),
        { value: 'custom', label: 'Свой', icon: 'pencil' }],
      value: this.f.preset,
      onSelect: (v) => {
        const p = PRESETS.find((x) => x.id === v);
        if (p) this.f = { ...this.f, ...p.f, preset: v };
        else this.f.preset = 'custom';
        this.#save();
        this.#buildTools();
        this.#fetch();
      },
    });
    this.dds = FIELDS.map((fd) => dropdown({
      className: 'fx-dd', lead: fd.lead, prefix: fd.prefix, title: fd.title,
      items: items(fd.opts), value: String(this.f[fd.key]),
      onSelect: (v) => {
        this.f[fd.key] = fd.opts.find(([x]) => String(x) === v)?.[0] ?? v;
        if (fd.key !== 'size' && fd.key !== 'exchange' && fd.key !== 'kind') {
          this.f.preset = 'custom';
          this.presetDD.setValue('custom');
        }
        this.#save();
        this.#fetch();
      },
    }));
    this.statusEl = el('span', { class: 'wm-status' });
    this.toolbar.append(this.presetDD, ...this.dds, el('span', { class: 'fx-spacer' }), this.statusEl);
  }

  #save() {
    settings.set(KEY, this.f);
  }

  #legend() {
    const sw = (cls, text) => el('span', { class: 'wm-leg-i' }, [el('i', { class: `wm-sw ${cls}` }), el('span', { text })]);
    return el('div', { class: 'wm-legend' }, [
      sw('ask', 'продажа — сопротивление (сверху)'),
      sw('bid', 'покупка — поддержка (снизу)'),
      sw('ring', 'толще контур — дольше стоит'),
      sw('pulse', 'пульсирует — цена подходит'),
      sw('ghost', 'призрак — снята/переставлена или съедена за 2 мин'),
      el('span', { class: 'wm-leg-i' }, [el('b', { class: 'wm-badge-dist', text: '0.8%' }), el('span', { text: 'до цены' })]),
      el('span', { class: 'wm-leg-i' }, [el('b', { class: 'wm-badge-age', text: '12' }), icon('fa-hourglass-half', 9), el('span', { text: 'минут живёт' })]),
    ]);
  }

  // ---------------------------------------------------------------- date

  async #fetch() {
    const f = this.f;
    const q = new URLSearchParams({
      maxDistance: f.maxDistance, minMinutes: f.minMinutes ?? 0, minAge: f.minAge, minUsd: f.minUsd,
      minNatr: f.minNatr, minVol: f.minVol, side: f.side, exchange: f.exchange, kind: f.kind || 'real', limit: 90,
    });
    try {
      const d = await (await fetch(`/api/walls?${q}`)).json();
      this.walls = d.walls || [];
      this.ended = d.ended || [];
      const b = d.books || {};
      const st = (k, n) => `${n} ${b[k]?.ready ?? 0}/${b[k]?.want ?? 0}`;
      this.statusEl.textContent = `стаканы: ${st('binance', 'Binance')} · ${st('bybit', 'Bybit')} · ${st('okx', 'OKX')} · на карте ${this.walls.length}`;
      this.#draw();
      this.onData?.(this);
    } catch (err) {
      console.warn('[wallmap]', err);
    }
  }

  async #fetchStats() {
    try {
      this.stats = await (await fetch('/api/walls/stats')).json();
    } catch {
      this.stats = null;
    }
    this.#renderStats();
  }

  // ------------------------------------------------------------- desen

  #geometry() {
    const W = this.stage.clientWidth;
    const H = this.stage.clientHeight;
    const R = Math.max(80, Math.min(W, H) / 2 - 34);
    return { W, H, R, cx: W / 2, cy: H / 2, D: Number(this.f.maxDistance) || 2 };
  }

  /** Raza pentru o distanță: rădăcină, ca zona de lângă preț (cea aglomerată) să fie mai largă. */
  #rad(d, g) {
    return g.R * Math.sqrt(Math.min(Math.abs(d), g.D) / g.D);
  }

  #drawRings(g) {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = this.svg;
    while (svg.firstChild) svg.firstChild.remove();
    svg.setAttribute('width', g.W);
    svg.setAttribute('height', g.H);
    const add = (tag, attrs) => {
      const n = document.createElementNS(ns, tag);
      for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, v);
      svg.append(n);
      return n;
    };
    // jumătățile: sus vânzări, jos cumpărări
    add('path', { d: `M ${g.cx - g.R} ${g.cy} A ${g.R} ${g.R} 0 0 1 ${g.cx + g.R} ${g.cy} Z`, class: 'wm-half ask' });
    add('path', { d: `M ${g.cx - g.R} ${g.cy} A ${g.R} ${g.R} 0 0 0 ${g.cx + g.R} ${g.cy} Z`, class: 'wm-half bid' });
    const inele = [0.25, 0.5, 1, 1.5, 2, 3].filter((x) => x <= g.D);
    for (const d of inele) {
      const r = this.#rad(d, g);
      add('circle', { cx: g.cx, cy: g.cy, r, class: `wm-ring${d === 1 ? ' one' : ''}` });
      for (const [x, y] of [[g.cx + r, g.cy], [g.cx - r, g.cy]]) {
        add('rect', { x: x - 15, y: y - 8, width: 30, height: 16, rx: 4, class: 'wm-ring-lbl-bg' });
        const t = add('text', { x, y: y + 4, class: 'wm-ring-lbl', 'text-anchor': 'middle' });
        t.textContent = `${d}%`;
      }
    }
    add('line', { x1: g.cx - g.R, y1: g.cy, x2: g.cx + g.R, y2: g.cy, class: 'wm-axis' });
    const sus = add('text', { x: g.cx, y: g.cy - g.R - 12, class: 'wm-half-lbl ask', 'text-anchor': 'middle' });
    sus.textContent = '▲ продажи · сопротивление';
    const jos = add('text', { x: g.cx, y: g.cy + g.R + 22, class: 'wm-half-lbl bid', 'text-anchor': 'middle' });
    jos.textContent = '▼ покупки · поддержка';
    add('circle', { cx: g.cx, cy: g.cy, r: 4, class: 'wm-center' });
  }

  #size(w) {
    if (this.f.size === 'usd') return 13 + 25 * Math.sqrt(Math.min(w.usd, 2e6) / 2e6);
    return 13 + 25 * Math.sqrt(Math.min(w.minutes, 60) / 60);
  }

  /** Pozițiile: unghi din nume, rază din distanță; suprapunerile se desfac doar pe unghi. */
  #layout(list, g) {
    const pts = list.map((w) => {
      const sus = w.side === 'ask';
      const h = hash01(`${w.symbol}|${w.ex}`);
      // sus: 195°..345°, jos: 15°..165° (y crește în jos)
      const a = (sus ? 195 + h * 150 : 15 + h * 150) * Math.PI / 180;
      return { w, a, r: Math.max(this.#rad(w.distance, g), 18), s: this.#size(w), sus };
    });
    for (let it = 0; it < 40; it++) {
      let mutat = false;
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const p = pts[i]; const q = pts[j];
          if (p.sus !== q.sus) continue;
          const dx = p.r * Math.cos(p.a) - q.r * Math.cos(q.a);
          const dy = p.r * Math.sin(p.a) - q.r * Math.sin(q.a);
          const dist = Math.hypot(dx, dy);
          const need = (p.s + q.s) * 0.92;
          if (dist >= need) continue;
          const pas = (need - dist) / Math.max(40, (p.r + q.r) / 2) / 2;
          const semn = p.a >= q.a ? 1 : -1;
          p.a += semn * pas; q.a -= semn * pas;
          mutat = true;
        }
      }
      // rămânem în semicercul nostru
      for (const p of pts) {
        const lo = (p.sus ? 188 : 8) * Math.PI / 180;
        const hi = (p.sus ? 352 : 172) * Math.PI / 180;
        p.a = Math.min(hi, Math.max(lo, p.a));
      }
      if (!mutat) break;
    }
    return pts;
  }

  #draw() {
    if (!this.stage?.isConnected) return;
    const g = this.#geometry();
    if (g.W < 50) return;
    this.#drawRings(g);
    // Pe ecran îngust cercul e mic: doar cele mai puternice 40.
    const vii = this.walls.slice(0, g.W < 520 ? 40 : 90);
    this.emptyEl.hidden = vii.length > 0;
    if (!vii.length) this.emptyEl.textContent = 'По этим фильтрам плотностей нет — ослабьте фильтры или выберите пресет «Все»';

    const pts = this.#layout(vii, g);
    const vazute = new Set();
    for (const p of pts) {
      const w = p.w;
      const k = `${w.ex}|${w.symbol}|${w.side}|${w.price}`;
      vazute.add(k);
      let n = this.nodes.get(k);
      if (!n) {
        n = { el: this.#bubble(w, k) };
        this.nodes.set(k, n);
        this.layer.append(n.el);
      }
      n.wall = w;
      this.#fill(n.el, w, p.s);
      const x = g.cx + p.r * Math.cos(p.a);
      const y = g.cy + p.r * Math.sin(p.a);
      n.el.style.transform = `translate(${x - p.s}px, ${y - p.s}px)`;
    }
    for (const [k, n] of this.nodes) {
      if (!vazute.has(k)) { n.el.remove(); this.nodes.delete(k); }
    }
    this.#ghosts(g);
  }

  #bubble(w, k) {
    const b = el('div', {
      class: `wm-b ${w.side}${k === this.selected ? ' sel' : ''}`,
      onmouseenter: () => this.#showTip(b),
      onmouseleave: () => { this.tip.hidden = true; },
      onclick: () => { this.setSelected(k); this.onSelect?.(this.nodes.get(k)?.wall || w, k); },
    }, [
      el('div', { class: 'wm-badges' }, [el('b', { class: 'wm-badge-dist' }), el('b', { class: 'wm-badge-age' })]),
      el('div', { class: 'wm-core' }, [
        el('span', { class: 'wm-usd' }),
        el('span', { class: 'wm-tk', text: baseTicker(w.symbol) }),
        el('span', { class: 'wm-icons' }, [iconMoneda(w.symbol, 12), iconBursa(w.ex, 11)]),
      ]),
    ]);
    return b;
  }

  #fill(b, w, s) {
    b.style.width = `${s * 2}px`;
    b.style.height = `${s * 2}px`;
    const age = w.age;
    const contur = age >= 1800 ? 4 : age >= 900 ? 3 : age >= 300 ? 2 : 1;
    b.style.setProperty('--ring', `${contur}px`);
    b.classList.toggle('fresh', age < 60);
    b.classList.toggle('near', !!w.approaching);
    b.classList.toggle('touched', !!w.touched);
    b.classList.toggle('small', s < 22);
    b.classList.toggle('mm', w.kind === 'mm');
    b.querySelector('.wm-badge-dist').textContent = `${Math.abs(w.distance).toFixed(2)}%`;
    b.querySelector('.wm-badge-age').textContent = `${min(age)}′`;
    b.querySelector('.wm-usd').textContent = fmtVolume(w.usd);
  }

  #ghosts(g) {
    this.layer.querySelectorAll('.wm-ghost').forEach((n) => n.remove());
    for (const w of this.ended.slice(-30)) {
      if (Math.abs(w.distance) > g.D) continue;
      const sus = w.side === 'ask';
      const a = (sus ? 195 : 15) + hash01(`${w.symbol}|${w.ex}`) * 150;
      const r = Math.max(this.#rad(w.distance, g), 18);
      const x = g.cx + r * Math.cos(a * Math.PI / 180);
      const y = g.cy + r * Math.sin(a * Math.PI / 180);
      this.layer.append(el('div', {
        class: `wm-ghost ${w.fate}`, style: { transform: `translate(${x - 11}px, ${y - 11}px)` },
        title: `${baseTicker(w.symbol)}: ${w.fate === 'pulled' ? 'снята или переставлена до подхода цены' : 'съедена'} · ${fmtVolume(w.peak)} · жила ${fmtAge(w.age)}`,
      }, [icon(w.fate === 'pulled' ? 'close' : 'bolt', 9)]));
    }
  }

  #showTip(b) {
    const k = [...this.nodes].find(([, n]) => n.el === b)?.[0];
    const w = k && this.nodes.get(k)?.wall;
    if (!w) return;
    const lung = w.side === 'bid';
    const rand = (kk, v) => el('div', { class: 'wm-tip-r' }, [el('span', { text: kk }), el('b', { text: v })]);
    clear(this.tip);
    this.tip.append(
      el('div', { class: 'wm-tip-h' }, [iconMoneda(w.symbol, 16), el('b', { text: baseTicker(w.symbol) }),
        iconBursa(w.ex, 13), el('span', { class: `wm-tip-side ${w.side}`, text: lung ? 'покупка · поддержка' : 'продажа · сопротивление' })]),
      rand('Цена плотности', fmtPrice(w.price)),
      rand('Объём', `${fmtVolume(w.usd)}${w.peak > w.usd * 1.05 ? ` (был ${fmtVolume(w.peak)}, съедено ${Math.round(w.eaten * 100)}%)` : ''}`),
      rand('Сила', `×${w.ratio} к обычному уровню · ${w.minutes >= 1 ? Math.round(w.minutes) : w.minutes.toFixed(1)} мин оборота`),
      rand('До цены', pct(w.distance)),
      rand('Живёт', fmtAge(w.age)),
      rand('Тип', w.kind === 'mm' ? `маркет-мейкер: переставлялась ${w.moves}× за ${fmtAge(w.chain_age || 0)}`
        : w.kind === 'young' ? 'молодая (<2 мин)' : 'реальная — стоит на месте'),
      rand('Ход монеты', w.natr ? `${Number(w.natr).toFixed(2)}% за 5м свечу` : '—'),
      rand('Оборот 24ч', w.vol24 ? fmtVolume(w.vol24) : '—'),
      el('div', { class: 'wm-tip-note', text: w.approaching ? 'Цена подходит к плотности.' : 'Клик — график монеты с плотностями.' }),
    );
    this.tip.hidden = false;
    const r = b.getBoundingClientRect();
    const s = this.stage.getBoundingClientRect();
    let x = r.right - s.left + 8;
    if (x + 260 > s.width) x = r.left - s.left - 268;
    this.tip.style.left = `${Math.max(4, x)}px`;
    this.tip.style.top = `${Math.max(4, Math.min(r.top - s.top - 10, s.height - 230))}px`;
  }

  // ------------------------------------------------------------ jurnal

  #renderStats() {
    clear(this.statsEl);
    const t = this.stats?.trades;
    const f = this.stats?.fates;
    const R = (x) => `${x >= 0 ? '+' : ''}${x.toFixed(2)}R`;
    const P = (x) => `${Math.round(x * 100)}%`;
    const head = el('div', { class: 'wm-stats-h' }, [icon('clipboard', 11), el('b', { text: 'Журнал плотностей' })]);
    if (!t?.all?.n && !f?.all?.n) {
      this.statsEl.append(head, el('span', {
        class: 'wm-stats-mute',
        text: 'Собирается: каждая плотность, к которой подходит цена, записывается — устояла, съедена или снята, '
          + 'и чем закончилась бы сделка от неё. Первые цифры — через пару часов.',
      }));
      return;
    }
    const parti = [head];
    if (t?.all?.n) {
      parti.push(el('span', {
        class: 'wm-stat',
        text: `сделок от плотности: ${t.all.n} · ${P(t.all.plus)} в плюс · ${R(t.all.E)} на сделку · отскок ≥1%: ${P(t.all.bounce1)}`,
      }));
    }
    if (f?.all?.n) {
      parti.push(el('span', {
        class: 'wm-stat warn',
        text: `снимают или переставляют до подхода цены: ${P(f.all.pulled)} из ${f.all.n} плотностей`,
      }));
    }
    const det = el('details', { class: 'wm-det' }, [el('summary', { text: 'подробно' })]);
    const tabel = (titlu, rows, cell) => el('div', { class: 'wm-tbl' }, [
      el('div', { class: 'wm-tbl-t', text: titlu }),
      ...rows.map((r) => el('div', { class: 'wm-tbl-r' }, [el('span', { text: r.name }), el('b', { text: r.n ? cell(r) : '—' })])),
    ]);
    const cellT = (r) => `${r.n} сд. · ${R(r.E)} · ≥1%: ${P(r.bounce1)}`;
    if (t?.all?.n) {
      det.append(tabel('Сделки по возрасту плотности', t.by_age, cellT), tabel('По силе', t.by_ratio, cellT),
        tabel('По ходу монеты', t.by_natr, cellT), tabel('Чем кончилась плотность', t.by_fate, cellT));
    }
    if (f?.all?.n) det.append(tabel('Как часто снимают', f.by_age, (r) => `${r.n} · снято ${P(r.pulled)} · съедено ${P(r.eaten)}`));
    parti.push(det);
    this.statsEl.append(...parti);
  }
}
